// Blender-authored brass gunship: layered hull, glazed canopy, twin nacelles
// and emissive thrust cones (the §e recipe: emissive cone + bloom-free
// HDR glow, length and brightness by state), tail fin, landing skids.
// Synced from the world model each frame; the flown ship follows local
// prediction, everyone else's follows the interpolated feed. Geometry and
// common materials are shared; the wear strip and thrust materials are
// per-entry (their color/intensity is per-hull state).

import * as THREE from "three";
import type { World } from "../core/world";
import { cloneAuthoredAsset, disposeAuthoredObject, getAuthoredTemplate } from "./authored-assets";
import { Tracker } from "./common";

/** Wear-band hull-strip look — the machine is the gauge (skiff idiom). */
const STRIP_LOOK: Record<string, number> = {
  true: 0xd8b04a,
  weathered: 0xc9a24a,
  strained: 0xb08d40,
  laboring: 0x9aa845,
  battered: 0x5f6b58,
};

const THRUST_IDLE = 0x3a86a8;
const THRUST_BOOST = 0xffc46b;

interface ShipEntry {
  root: THREE.Group;
  strip: THREE.MeshStandardMaterial;
  thrust: THREE.MeshStandardMaterial;
  cones: THREE.Mesh[];
}

interface SharedParts {
  fuselage: THREE.BufferGeometry;
  canopy: THREE.BufferGeometry;
  nacelle: THREE.BufferGeometry;
  cone: THREE.BufferGeometry;
  fin: THREE.BufferGeometry;
  skid: THREE.BufferGeometry;
  strip: THREE.BufferGeometry;
  brass: THREE.Material;
  dark: THREE.Material;
}

function buildShipMesh(
  parts: SharedParts,
  strip: THREE.MeshStandardMaterial,
  thrust: THREE.MeshStandardMaterial,
): { root: THREE.Group; cones: THREE.Mesh[] } {
  const g = new THREE.Group();

  // The editable Blender hull has a ground origin; the flight integrator
  // keeps its original centre and all existing pilot/engine attachment points.
  if (getAuthoredTemplate("aether-gunship")) {
    const hull = cloneAuthoredAsset("aether-gunship");
    hull.position.y = -0.98;
    const replaced = new Set<THREE.Material>();
    hull.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return;
      const bindBurner = (material: THREE.Material): THREE.Material => {
        if (material.name !== "vehicle-aether") return material;
        replaced.add(material);
        return thrust;
      };
      object.material = Array.isArray(object.material)
        ? object.material.map(bindBurner)
        : bindBurner(object.material);
    });
    // The source's recessed burner cores share the same live material as the
    // exhaust, so a dead or boosted machine reads correctly from every angle.
    for (const material of replaced) material.dispose();
    g.add(hull);
    const cones: THREE.Mesh[] = [];
    for (const side of [-1, 1]) {
      const gauge = new THREE.Mesh(parts.strip, strip);
      gauge.scale.set(0.016, 0.65, 0.8);
      gauge.position.set(side * 1.095, 0.45, -0.55);
      g.add(gauge);
      const cone = new THREE.Mesh(parts.cone, thrust);
      cone.rotation.x = -Math.PI / 2;
      cone.position.set(side * 1.75, 0.05, -3.5);
      g.add(cone);
      cones.push(cone);
    }
    g.traverse((object) => {
      if ((object as THREE.Mesh).isMesh) {
        object.castShadow = (object as THREE.Mesh).material !== thrust;
        object.receiveShadow = true;
      }
    });
    return { root: g, cones };
  }

  const fuselage = new THREE.Mesh(parts.fuselage, parts.brass);
  fuselage.position.y = 0.2;
  g.add(fuselage);

  const canopy = new THREE.Mesh(parts.canopy, parts.dark);
  canopy.position.set(0, 0.78, 1.5);
  canopy.rotation.x = -0.18;
  g.add(canopy);

  const stripMesh = new THREE.Mesh(parts.strip, strip);
  stripMesh.position.set(0, 0.62, -0.4);
  g.add(stripMesh);

  const fin = new THREE.Mesh(parts.fin, parts.brass);
  fin.position.set(0, 1.05, -2.6);
  g.add(fin);

  const cones: THREE.Mesh[] = [];
  for (const side of [-1.75, 1.75]) {
    const nacelle = new THREE.Mesh(parts.nacelle, parts.dark);
    nacelle.rotation.x = Math.PI / 2;
    nacelle.position.set(side, 0.05, -0.3);
    g.add(nacelle);

    const cone = new THREE.Mesh(parts.cone, thrust);
    cone.rotation.x = -Math.PI / 2; // apex trailing aft (−z)
    cone.position.set(side, 0.05, -2.6);
    g.add(cone);
    cones.push(cone);

    const skid = new THREE.Mesh(parts.skid, parts.dark);
    skid.position.set(side * 0.62, -0.72, 0.2);
    g.add(skid);
  }
  return { root: g, cones };
}

export class ShipLayer {
  /** Parent under the area's world anchor. */
  readonly group = new THREE.Group();
  private tracker = new Tracker();
  private parts: SharedParts;
  private entries = new Map<string, ShipEntry>();
  private elapsed = 0;

  constructor() {
    const t = this.tracker;
    this.parts = {
      fuselage: t.geo(new THREE.BoxGeometry(2.2, 1.0, 6.6)),
      canopy: t.geo(new THREE.BoxGeometry(1.3, 0.5, 1.9)),
      nacelle: t.geo(new THREE.CylinderGeometry(0.42, 0.5, 4.4, 10)),
      cone: t.geo(new THREE.ConeGeometry(0.4, 1.8, 10)),
      fin: t.geo(new THREE.BoxGeometry(0.14, 1.2, 1.5)),
      skid: t.geo(new THREE.BoxGeometry(0.2, 0.5, 2.6)),
      strip: t.geo(new THREE.BoxGeometry(2.26, 0.08, 3.2)),
      brass: t.mat(
        new THREE.MeshStandardMaterial({ color: 0x8a6a3a, metalness: 0.65, roughness: 0.38 }),
      ),
      dark: t.mat(new THREE.MeshStandardMaterial({ color: 0x1c1a22, roughness: 0.8 })),
    };
  }

  sync(world: World, dt: number): void {
    this.elapsed += dt;

    for (const [id, entry] of this.entries) {
      if (!world.ships.has(id)) {
        this.group.remove(entry.root);
        disposeAuthoredObject(entry.root);
        entry.strip.dispose();
        entry.thrust.dispose();
        this.entries.delete(id);
      }
    }

    for (const [id, ship] of world.ships) {
      let entry = this.entries.get(id);
      if (!entry) {
        const strip = new THREE.MeshStandardMaterial({
          color: 0xd8b04a,
          emissive: 0xd8b04a,
          emissiveIntensity: 0.7,
        });
        const thrust = new THREE.MeshStandardMaterial({
          color: THRUST_IDLE,
          emissive: THRUST_IDLE,
          emissiveIntensity: 0.8,
          transparent: true,
          opacity: 0.85,
        });
        const built = buildShipMesh(this.parts, strip, thrust);
        entry = { root: built.root, strip, thrust, cones: built.cones };
        this.entries.set(id, entry);
        this.group.add(entry.root);
      }

      const mine = world.selfShip?.id === id;
      const pose = mine && world.selfShip ? world.selfShip.pose : undefined;
      const x = pose ? pose.x : ship.renderPose.x;
      const y = pose ? pose.y : ship.renderPose.y;
      const z = pose ? pose.z : ship.renderPose.z;
      const yaw = pose ? pose.yaw : ship.renderPose.yaw;
      const speed = Math.abs(pose ? pose.speed : ship.speed);

      const bob = ship.riderId ? 0 : Math.sin(this.elapsed * 1.1 + x * 0.03) * 0.04;
      entry.root.position.set(x, y + bob, z);
      entry.root.rotation.y = yaw;
      // A touch of bank into the motion sells flight without a real roll
      // axis in the sim: lean by yaw rate is unavailable here, so lean by
      // speed alone, subtle.
      entry.root.rotation.z = 0;

      const look = STRIP_LOOK[ship.wearBand] ?? 0xd8b04a;
      entry.strip.color.setHex(look);
      entry.strip.emissive.setHex(look);
      entry.strip.emissiveIntensity = ship.chargeBand === "dry" ? 0.12 : 0.7;

      // Thrust: length and brightness carry speed; boost goes hot amber.
      const burn = ship.adrift ? 0 : Math.min(1, speed / 60);
      const boosting = ship.boosting && !ship.adrift;
      entry.thrust.color.setHex(boosting ? THRUST_BOOST : THRUST_IDLE);
      entry.thrust.emissive.setHex(boosting ? THRUST_BOOST : THRUST_IDLE);
      entry.thrust.emissiveIntensity =
        (ship.adrift ? 0.08 : 0.6) + burn * 2.4 + (boosting ? 2.2 : 0);
      const stretch = 0.5 + burn * 1.6 + (boosting ? 1.1 : 0);
      for (const cone of entry.cones) {
        cone.scale.set(1, stretch, 1);
        cone.position.z = (getAuthoredTemplate("aether-gunship") ? -2.58 : -2.1) - stretch * 0.9;
      }
    }
  }

  dispose(): void {
    for (const entry of this.entries.values()) {
      this.group.remove(entry.root);
      disposeAuthoredObject(entry.root);
      entry.strip.dispose();
      entry.thrust.dispose();
    }
    this.entries.clear();
    this.tracker.dispose();
  }
}
